import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageCircle, CheckCircle, RefreshCw,
  Smartphone, Save, Eye, EyeOff, Wifi, WifiOff, Unlink2, Copy,
  ExternalLink, AlertTriangle, Phone
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

function formatBotPhone(phone) {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
  if (d.length >= 10)  return `+${d}`;
  return `+${d}`;
}

/* ─── Phone mask ─────────────────────────────────────────── */
function maskPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 13);
  if (d.length <= 2)  return `+${d}`;
  if (d.length <= 4)  return `+${d.slice(0,2)} (${d.slice(2)}`;
  if (d.length <= 9)  return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4)}`;
  if (d.length <= 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  return v;
}

function rawPhone(v) {
  return v.replace(/\D/g, '');
}

/* ─── Main page (tenant view) ────────────────────────────── */
export default function WhatsAppBot() {
  const { user } = useAuth();

  // Bot status (read-only for tenant)
  const [botOnline, setBotOnline] = useState(null); // null = loading, true/false = state
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [botPhone, setBotPhone] = useState('');

  // User phone registration
  const [myPhone, setMyPhone] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [verificado, setVerificado] = useState(false);
  const [codigoVinculacao, setCodigoVinculacao] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await api.get('/whatsapp/status');
      setBotOnline(res.data?.instance?.state === 'open');
      if (res.data?.phone) setBotPhone(res.data.phone);
    } catch {
      setBotOnline(false);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const fetchMyPhone = useCallback(async () => {
    try {
      const res = await api.get('/whatsapp/my-phone');
      const p = res.data?.whatsappNumero || '';
      setMyPhone(p);
      setPhoneInput(p ? maskPhone(p) : '');
      setVerificado(!!res.data?.verificado);
      setCodigoVinculacao(res.data?.codigoVinculacao || null);
    } catch {}
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchMyPhone();
  }, [fetchStatus, fetchMyPhone]);

  const handleUnlink = async () => {
    if (!window.confirm('Desvincular o WhatsApp atual e gerar um novo código? Você precisará enviar /vincular novamente.')) {
      return;
    }
    try {
      const res = await api.post('/whatsapp/unlink');
      setVerificado(false);
      setCodigoVinculacao(res.data?.codigoVinculacao || null);
    } catch (err) {
      console.error(err);
      alert('Não foi possível desvincular. Tente novamente.');
    }
  };

  const handleSavePhone = async () => {
    const raw = rawPhone(phoneInput);
    if (raw.length < 10) return;
    setSavingPhone(true);
    setPhoneSaved(false);
    try {
      const res = await api.post('/whatsapp/register-phone', { whatsappNumero: raw });
      setMyPhone(raw);
      setVerificado(!!res.data?.verificado);
      setCodigoVinculacao(res.data?.codigoVinculacao || null);
      setPhoneSaved(true);
      setTimeout(() => setPhoneSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <MessageCircle size={22} className="text-green-500" />
            Assistente WhatsApp
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Lance gastos e receitas direto pelo WhatsApp.
          </p>
        </div>
        <button onClick={fetchStatus} className="fin-btn-secondary text-xs" title="Atualizar status">
          <RefreshCw size={13} className={loadingStatus ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5">
        <p className="font-semibold text-green-800 mb-3 text-sm">Como funciona</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { step: '1', icon: '📱', text: 'Cadastre seu número abaixo' },
            { step: '2', icon: '💬', text: 'Clique no link do bot para abrir o WhatsApp e enviar /vincular' },
            { step: '3', icon: '✅', text: 'A IA interpreta e lança direto no seu painel' },
          ].map(s => (
            <div key={s.step} className="flex items-start gap-2.5">
              <div className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {s.step}
              </div>
              <p className="text-xs text-green-800">
                <span className="mr-1">{s.icon}</span>{s.text}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4 bg-white/70 rounded-xl p-3 text-xs text-green-900 font-mono">
          <p className="font-semibold mb-1 font-sans text-green-700">Exemplos de mensagens:</p>
          <p>• <em>"gastei 150 no mercado"</em></p>
          <p>• <em>"recebi 2000 de cliente hoje"</em></p>
          <p>• <em>"paguei 89 reais de streaming"</em></p>
          <p>• <em>"transferi 500 do Nubank pro Inter"</em></p>
          <p>• <em>"qual o saldo do Cora?"</em></p>
          <p>• <em>"quanto tenho no total?"</em></p>
        </div>
      </div>

      {/* Simple bot status — read-only, no QR */}
      <div className="fin-card p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                botOnline ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
              }`}
            >
              {botOnline ? <Wifi size={18} /> : <WifiOff size={18} />}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {loadingStatus
                  ? 'Verificando...'
                  : botOnline
                  ? 'Assistente disponível'
                  : 'Assistente temporariamente indisponível'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {botOnline
                  ? 'Cadastre seu número abaixo para começar.'
                  : 'Tente novamente em alguns minutos.'}
              </p>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
              loadingStatus
                ? 'bg-slate-100 text-slate-500'
                : botOnline
                ? 'bg-emerald-50 text-emerald-600'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                loadingStatus ? 'bg-slate-400' : botOnline ? 'bg-emerald-500' : 'bg-slate-400'
              }`}
            />
            {loadingStatus ? '...' : botOnline ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Bot phone number + direct link */}
      <div className="fin-card overflow-hidden">
        {/* Green top bar */}
        <div className="h-1 bg-gradient-to-r from-green-400 to-emerald-500" />
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
              <Phone size={16} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Número do Bot Gastify
              </p>
              <p className="text-xs text-slate-400">Adicione este número e envie uma mensagem para começar</p>
            </div>
          </div>

          {botPhone ? (
            <>
              {/* Phone number display */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl mb-3">
                <MessageCircle size={22} className="text-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400 mb-0.5">Envie mensagem para:</p>
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100 tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatBotPhone(botPhone)}
                  </p>
                </div>
                <button
                  onClick={() => navigator.clipboard?.writeText(formatBotPhone(botPhone))}
                  className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                  title="Copiar número"
                >
                  <Copy size={14} />
                </button>
              </div>

              {/* Direct WhatsApp link */}
              <a
                href={`https://wa.me/${botPhone.replace(/\D/g, '')}?text=${encodeURIComponent('/vincular')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-semibold text-sm text-white transition-opacity hover:opacity-90"
                style={{ background: '#25D366' }}
              >
                <ExternalLink size={16} />
                Abrir conversa no WhatsApp
              </a>
              <p className="text-center text-xs text-slate-400 mt-2">
                O link já abre com o comando <strong className="text-slate-500">/vincular</strong> pronto para enviar
              </p>

              {/* Warning about number change */}
              <div className="mt-4 flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  <strong>Atenção:</strong> o número do bot pode mudar com o tempo conforme atualizações do sistema.
                  Sempre verifique aqui o número atual antes de entrar em contato.
                </p>
              </div>
            </>
          ) : (
            <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl text-center">
              <p className="text-sm text-slate-400">
                {loadingStatus
                  ? '⏳ Carregando número do bot...'
                  : botOnline
                  ? '📡 Bot online — número sendo carregado. Tente atualizar em instantes.'
                  : '⚠️ Bot offline. Número indisponível no momento.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* My phone registration */}
      <div className="fin-card p-5">
        <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm mb-1 flex items-center gap-2">
          <Smartphone size={16} /> Meu número do WhatsApp
        </p>
        <p className="text-xs text-slate-400 mb-4">
          Cadastre o número pessoal que vai usar para enviar lançamentos ao bot.
        </p>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              className="fin-input w-full pr-10"
              placeholder="+55 (11) 99999-9999"
              value={phoneInput}
              onChange={e => setPhoneInput(maskPhone(e.target.value))}
              type={showPhone ? 'text' : 'password'}
            />
            <button
              type="button"
              onClick={() => setShowPhone(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPhone ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <button
            onClick={handleSavePhone}
            disabled={savingPhone || rawPhone(phoneInput).length < 10}
            className="fin-btn-primary text-xs flex items-center gap-1.5 flex-shrink-0"
          >
            {phoneSaved
              ? <><CheckCircle size={13} /> Salvo!</>
              : savingPhone
              ? 'Salvando...'
              : <><Save size={13} /> Salvar</>
            }
          </button>
        </div>

        {myPhone && verificado && (
          <div className="mt-3 flex items-center justify-between gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
            <p className="text-xs text-emerald-700 flex items-center gap-1.5">
              <CheckCircle size={14} className="text-emerald-600" />
              Número <strong>vinculado</strong>: +{myPhone}
            </p>
            <button
              onClick={handleUnlink}
              className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-lg flex items-center gap-1 transition-colors"
              title="Desvincular e gerar novo código"
            >
              <Unlink2 size={12} /> Desvincular
            </button>
          </div>
        )}

        {myPhone && !verificado && codigoVinculacao && (
          <div className="mt-3 p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50">
            <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
              ⚠️ Falta um passo: vincule seu WhatsApp
            </p>
            <p className="text-xs text-amber-900 mb-3">
              Escaneie o QR Code abaixo com a câmera do seu celular. O WhatsApp vai abrir direto com a mensagem pronta — só toque em <strong>Enviar</strong>.
            </p>

            {/* QR Code — encodes WhatsApp deep link with pre-filled /vincular command */}
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-3">
              <div className="bg-white border border-amber-200 rounded-xl p-2 flex-shrink-0">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=4&data=${encodeURIComponent(`https://wa.me/${botPhone}?text=/vincular ${codigoVinculacao}`)}`}
                  alt="QR Code para vincular WhatsApp"
                  className="w-40 h-40 rounded-lg"
                />
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <p className="text-xs text-amber-800 font-semibold">Ou copie o comando manualmente:</p>
                <div className="bg-white border border-amber-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                  <p className="font-mono text-sm font-bold text-amber-900 select-all flex-1">
                    /vincular {codigoVinculacao}
                  </p>
                  <button
                    onClick={() => navigator.clipboard?.writeText(`/vincular ${codigoVinculacao}`)}
                    className="text-amber-700 hover:text-amber-900 p-1 rounded hover:bg-amber-100"
                    title="Copiar comando"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-[11px] text-amber-700">
                  Depois disso, você poderá enviar gastos e receitas livremente pelo WhatsApp.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
          <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">⚠️ Importante</p>
          <ul className="text-xs text-slate-400 space-y-0.5">
            <li>• Use o mesmo número que você utiliza no WhatsApp pessoal</li>
            <li>• Apenas você (com esse número) terá acesso ao seu painel</li>
            <li>• Seus dados só serão lançados na sua conta Gastify</li>
          </ul>
        </div>
      </div>

      {/* Example conversation */}
      <div className="fin-card p-5">
        <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm mb-4">
          💬 Exemplo de conversa
        </p>
        <div className="space-y-3 text-sm">
          {[
            { from: 'user',  text: 'gastei 340 de energia elétrica' },
            { from: 'bot',   text: '📋 Confirma o lançamento?\n\n💸 DESPESA: R$ 340,00\n📝 Energia elétrica\n🏷️ Contas de Casa\n📅 hoje\n\nResponda Sim para confirmar ou Não para cancelar.' },
            { from: 'user',  text: 'Sim' },
            { from: 'bot',   text: 'Em qual carteira deseja registrar?\n\n1️⃣ Nubank\n2️⃣ Itaú\n0️⃣ Sem carteira' },
            { from: 'user',  text: '1' },
            { from: 'bot',   text: '✅ Lançado com sucesso!\n\n💸 Despesa: R$ 340,00\n🏷️ Contas de Casa\n🏦 Nubank\n📅 17/05/2026' },
          ].map((m, i) => (
            <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] px-3 py-2 rounded-2xl text-xs whitespace-pre-line ${
                  m.from === 'user'
                    ? 'bg-green-500 text-white rounded-br-sm'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-bl-sm'
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
